import {
    useWizardContext,
    useFormContext,
} from "@mallardbay/lib-react-components"

import { isSelfOnboardingStep } from "~components/self-onboard/helpers/onboarding-helpers"
import { useLogOutfitterSelfOnboardingEvent } from "~utils/hooks/use-log-outfitter-self-onboarding-event"
import { useUpdateSelfOnboardingStep } from "~utils/hooks/use-update-self-onboarding-step"

export function useSelfOnboardNavigation({
    isMainNavigation = false,
}: {
    isMainNavigation?: boolean
}) {
    const { logStepCompleted } = useLogOutfitterSelfOnboardingEvent()
    const { currentNavigationStep } = useWizardContext()
    const { updateSelfOnboardingStep, isLoading: isStepUpdateLoading } =
        useUpdateSelfOnboardingStep()
    const isFormStepLoading = useFormStepIsLoading()
    const isLoading = isStepUpdateLoading || isFormStepLoading

    const isFormStep = currentNavigationStep?.hasForm

    const shouldNavigateToNextStep = isMainNavigation ? !isFormStep : true

    if (!currentNavigationStep) {
        return {}
    }

    const nextType = currentNavigationStep.nextType
    const previousType = currentNavigationStep.previousType

    const isNextStepValid = isSelfOnboardingStep(nextType)
    const isPreviousStepValid = isSelfOnboardingStep(previousType)

    const onSuccess = () => logStepCompleted(currentNavigationStep.type)
    const handleNextClick =
        isNextStepValid && shouldNavigateToNextStep
            ? () => {
                  void updateSelfOnboardingStep({
                      step: nextType,
                      onSuccess,
                  })
              }
            : undefined

    const handleBackClick = isPreviousStepValid
        ? () => {
              void updateSelfOnboardingStep({ step: previousType })
          }
        : undefined

    // Skipping advances to the next step without submitting the current step's form
    const handleSkipClick = isNextStepValid
        ? () => {
              void updateSelfOnboardingStep({ step: nextType })
          }
        : undefined

    return { handleNextClick, handleBackClick, handleSkipClick, isLoading }
}

export function useFormStepIsLoading() {
    const formContext = useFormContext()

    // could be falsey if we are not in a form context
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!formContext) return false

    const {
        formState: { isLoading, isSubmitting },
    } = formContext

    return isLoading || isSubmitting
}
